import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Typography,
} from "@material-ui/core";
import { FC, useEffect, useRef, useState } from "react";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import FormikForm, { FormikStep } from "src/components/ui-components/FormikForm";
import InputField from "src/components/ui-components/InputField";
import SelectField from "src/components/ui-components/SelectField";
import { IUserGroupData } from "./Types/MobileUserGroup";
import * as Yup from "yup";
import {
  GetMobileUserGroupByKey,
  InsertUpdateMobileUserGroup
} from "./ConnectionService";

interface ModalProps {
  open: boolean;
  handleClose: any;
  userGroupKey?: string | null;
  mobileUserGroupList: IUserGroupData[];
  refreshData;
  mobileRoleCodeMaster: any;
}

const defaultForm: IUserGroupData = {
  UserGroupKey: null,
  userGroup: "",
  ClientRole: "",
  MobileRoleCode: "",
  ParentUserGroup: "",
  FetchChildData: false,
  IsDisabled: false,
  Is2Fa: false,
  parentUserGroupName: null,
};

const MobileUserGroupCreateEditModal: FC<ModalProps> = ({
  open,
  handleClose,
  userGroupKey,
  mobileUserGroupList,
  mobileRoleCodeMaster,
  refreshData,
}: ModalProps) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef(null);

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: userGroupKey && true },
      });
    }
  };

  const GetUserGroup = async () => {
    setIsFetching(true);
    const res = await GetMobileUserGroupByKey(userGroupKey);
    resetFormData({
      UserGroupKey: res.UserGroupKey,
      userGroup: res.userGroup,
      ClientRole: res.ClientRole,
      MobileRoleCode: res.MobileRoleCode,
      ParentUserGroup: res.ParentUserGroup ?? "",
      FetchChildData: res.FetchChildData ?? false,
      IsDisabled: res.IsDisabled,
      Is2Fa: res.Is2FA,
    });
    setIsFetching(false);
  };

  useEffect(() => {
    if (userGroupKey) {
      GetUserGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroupKey]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Mobile User Group Create Edit"
        aria-describedby="Create Edit Form for Mobile User Group"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <>
          <Box
            sx={{
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              width: "70%",
            }}
          >
            <Typography
              sx={{ marginBottom: "2%", fontWeight: "bold" }}
              variant="h5"
            >
              {`${userGroupKey ? "Edit" : "Create"} User Group`}
            </Typography>
            {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
            <Box sx={{ display: isFetching ? "none" : "" }}>
              <FormikForm
                innerRef={formRef}
                initialValues={defaultForm}
                onSubmit={async (values): Promise<void> => {
                  setIsLoading(true);
                  if (values.ParentUserGroup === null || values.ParentUserGroup === "") {
                    values.ParentUserGroup = null;
                    values.FetchChildData = false;
                  }
                  try {
                    const res = await InsertUpdateMobileUserGroup(values);
                    if (res.data.isSuccess) {
                      setTimeout(() => {
                        handleClose();
                        refreshData();
                        setIsLoading(false);
                      }, 2000);
                    } else {
                      setIsLoading(false);
                    }
                  } catch (error) {
                    setIsLoading(false);
                  }
                }}
              >
                <FormikStep
                  validationSchema={Yup.object({
                    userGroup: Yup.string()
                      .nullable()
                      .max(150, "User Group name cannot be more than 150 characters")
                      .required("User Group name is required")
                      .matches(
                        /^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/,
                        "Please enter valid User Group name"
                      ),
                    ClientRole: Yup.string()
                      .nullable()
                      .max(100, "Client Role cannot be more than 100 characters")
                      .required("Client Role is required")
                      .matches(
                        /^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/,
                        "Please enter valid Client Role"
                      ),
                    MobileRoleCode: Yup.string()
                      .nullable()
                      .max(100, "Mobile Role Code cannot be more than 100 characters")
                      .required("Mobile Role Code is required")
                      .matches(
                        /^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/,
                        "Please enter valid Mobile Role Code"
                      )
                      .test(
                        'mobileRoleCodeValidation',
                        'Mobile Role Code already exists',
                        (value, { createError, parent }) => {
                          if (
                            mobileUserGroupList.some(
                              (group) =>
                                group.MobileRoleCode?.toLowerCase() === value?.toLowerCase() &&
                                group.UserGroupKey !== parent.UserGroupKey
                            )
                          ) {
                            return createError({ message: "Mobile Role Code already exists" });
                          }
                          return true;
                        }
                      ),
                    ParentUserGroup: Yup.string().nullable(),
                    Is2Fa: Yup.bool().nullable(),
                    FetchChildData: Yup.bool().nullable(),
                    IsDisabled: Yup.bool().nullable(),
                  })}
                >
                  <Grid spacing={2} container>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                      <InputField label="User Group *" name="userGroup" />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                      <InputField label="Client Role *" name="ClientRole" />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                      <SelectField
                        data={userGroupKey ? mobileUserGroupList?.filter((x) => x.UserGroupKey !== userGroupKey) : mobileUserGroupList}
                        dataTextField="userGroup"
                        dataValueField="UserGroupKey"
                        name="ParentUserGroup"
                        label="Parent User Group"
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                      <SelectField
                        data={mobileRoleCodeMaster}
                        dataTextField="mobileRoleCodeDisplayName"
                        dataValueField="mobileRoleCode"
                        name="MobileRoleCode"
                        label="Mobile Role Code *"
                      />
                    </Grid>
                  </Grid>
                  <Grid spacing={2} container justifyContent="end" sx={{ mt: 2 }}>
                    <Button
                      sx={{
                        minWidth: 100,
                        marginRight: "2%",
                        color: "rgb(107, 119, 140)",
                      }}
                      disabled={isLoading}
                      variant="outlined"
                      type="button"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{
                        background: "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100
                      }}
                      disabled={isLoading}
                      variant="contained"
                    >
                      Save
                      {isLoading && (<CircularProgress color="inherit" thickness={5} size={20} sx={{ ml: 1 }} />)}
                    </Button>
                  </Grid>
                </FormikStep>
              </FormikForm>
            </Box>
          </Box>
        </>
      </Modal>
    </>
  );
};

export default MobileUserGroupCreateEditModal;
